import { useState, useCallback } from 'react';
import { Mode } from './types';

export function useCopyDay(timelineMatrix: Mode[][], markerMatrix: number[][]) {
  const [sourceDayForCopy, setSourceDayForCopy] = useState<number | null>(null);
  const [targetDaysForCopy, setTargetDaysForCopy] = useState<number[]>([]);
  const [originalConfigurations, setOriginalConfigurations] = useState<{
    [key: number]: { timeline: Mode[]; markers: number[] };
  }>({});

  const copyDayConfiguration = useCallback(
    (sourceDayIndex: number, targetDayIndex: number) => {
      if (!originalConfigurations[targetDayIndex]) {
        setOriginalConfigurations((prev) => ({
          ...prev,
          [targetDayIndex]: {
            timeline: [...timelineMatrix[targetDayIndex]],
            markers: [...markerMatrix[targetDayIndex]],
          },
        }));
      }
      const sourceTimeline = [...timelineMatrix[sourceDayIndex]];
      const sourceMarkers = [...markerMatrix[sourceDayIndex]];
      const newTimelineMatrix = [...timelineMatrix];
      const newMarkerMatrix = [...markerMatrix];
      newTimelineMatrix[targetDayIndex] = [...sourceTimeline];
      newMarkerMatrix[targetDayIndex] = [...sourceMarkers];
      // These setters should be called in the parent component
      return { newTimelineMatrix, newMarkerMatrix };
    },
    [timelineMatrix, markerMatrix, originalConfigurations]
  );

  const restoreOriginalConfiguration = useCallback(
    (dayIndex: number) => {
      const savedConfig = originalConfigurations[dayIndex];
      if (savedConfig) {
        const newTimelineMatrix = [...timelineMatrix];
        const newMarkerMatrix = [...markerMatrix];
        newTimelineMatrix[dayIndex] = [...savedConfig.timeline];
        newMarkerMatrix[dayIndex] = [...savedConfig.markers];
        const newConfigs = { ...originalConfigurations };
        delete newConfigs[dayIndex];
        setOriginalConfigurations(newConfigs);
        return { newTimelineMatrix, newMarkerMatrix };
      }
      return null;
    },
    [timelineMatrix, markerMatrix, originalConfigurations]
  );

  const handleDaySelection = useCallback(
    (dayIndex: number, currentInteractionMode: string) => {
      if (currentInteractionMode === 'copy-day') {
        if (sourceDayForCopy === null) {
          setSourceDayForCopy(dayIndex);
          setTargetDaysForCopy([]);
        } else if (sourceDayForCopy === dayIndex) {
          setSourceDayForCopy(null);
          setTargetDaysForCopy([]);
        } else if (targetDaysForCopy.includes(dayIndex)) {
          setTargetDaysForCopy((prev) =>
            prev.filter((day) => day !== dayIndex)
          );
          return restoreOriginalConfiguration(dayIndex);
        } else {
          setTargetDaysForCopy((prev) => [...prev, dayIndex]);
          return copyDayConfiguration(sourceDayForCopy, dayIndex);
        }
      }
      return null;
    },
    [
      sourceDayForCopy,
      targetDaysForCopy,
      copyDayConfiguration,
      restoreOriginalConfiguration,
    ]
  );

  const resetCopyMode = useCallback(() => {
    setSourceDayForCopy(null);
    setTargetDaysForCopy([]);
    setOriginalConfigurations({});
  }, []);

  return {
    sourceDayForCopy,
    targetDaysForCopy,
    originalConfigurations,
    setSourceDayForCopy,
    setTargetDaysForCopy,
    setOriginalConfigurations,
    copyDayConfiguration,
    restoreOriginalConfiguration,
    handleDaySelection,
    resetCopyMode,
  };
}
